import { useCallback, useEffect, useRef, useState } from "react";

export default function useRolloverExecution({
  targetId,
  rollOverTaxComputation,
}) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (status === "complete") {
      const timer = setTimeout(() => window.location.reload(), 1500);
      return () => clearTimeout(timer);
    }
    if (status !== "processing") return;
    const preventUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [status]);

  const execute = useCallback(
    async ({ sourceComputation, rolloverType }) => {
      if (inFlight.current) return;
      if (
        !targetId ||
        !sourceComputation?.tc_id ||
        rolloverType !== "partial"
      ) {
        setError(
          "The rollover source or destination is unavailable. Reopen the computation and try again."
        );
        setStatus("error");
        return;
      }
      inFlight.current = true;
      setError("");
      setStatus("processing");
      try {
        await rollOverTaxComputation({
          tc_id: targetId,
          roll_over_tc_id: sourceComputation.tc_id,
          rollOverType: rolloverType,
        });
      } catch (requestError) {
        inFlight.current = false;
        if (!mounted.current) return;
        setError(
          typeof requestError === "string"
            ? requestError
            : requestError?.response?.data?.message ||
                requestError?.message ||
                "We could not confirm that the rollover completed."
        );
        setStatus("error");
        return;
      }

      // A browser-storage failure must not turn a committed rollover into an error.
      try {
        sessionStorage.setItem(
          "rolloverCompleted",
          JSON.stringify({ tc_id: targetId })
        );
      } catch (_storageError) {
        // Completion remains visible even if the post-refresh reminder cannot be stored.
      }
      if (mounted.current) setStatus("complete");
    },
    [targetId, rollOverTaxComputation]
  );

  return {
    status,
    error,
    execute,
    busy: status === "processing" || status === "complete",
    reset: () => {
      if (inFlight.current) return;
      setStatus("idle");
      setError("");
    },
  };
}
